import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 */
class CartDrawerComponent extends DialogComponent {
  #summaryThreshold = 0.5;
  #historyAbortController = null;
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    // Watch for DOM changes to hide elements if cart updates
    this.#initObserver();

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#observer?.disconnect();
  }

  #initObserver() {
    this.#observer = new MutationObserver(() => this.#hideDiscountElements());
    this.#observer.observe(this, { childList: true, subtree: true });
  }

  #hideDiscountElements() {
    const targetHandle = '25-summer-pickup-discount';
    // Using the specific structure provided in your source
    const cartRows = this.querySelectorAll('.cart-items__table-row');

    cartRows.forEach(row => {
      const link = row.querySelector('.cart-items__title')?.getAttribute('href');
      
      if (link && link.includes(targetHandle)) {
        // Hide price area
        const priceWrapper = row.querySelector('.cart-items__unit-price-wrapper');
        if (priceWrapper) priceWrapper.style.display = 'none';

        // Hide quantity controls and remove button
        const qtyWrapper = row.querySelector('.quantity-selector-wrapper');
        const removeBtn = row.querySelector('.cart-items__remove');
        if (qtyWrapper) qtyWrapper.style.display = 'none';
        if (removeBtn) removeBtn.style.display = 'none';
      }
    });
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;
    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }
    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) history.back();
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) this.showDialog();
    this.#announceCartCount(event.detail.resource?.item_count);
    this.#hideDiscountElements();
  };

  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;
    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();
    this.#hideDiscountElements();
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {any} */ (this.refs);
    if (!dialog) return;
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');
    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }
    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}